import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, CheckCircle, Circle, Clock, AlertTriangle,
  ExternalLink, Download, Upload, ChevronDown, ChevronRight,
  Phone, Mail, FileText, DollarSign, Calendar, Shield,
  Play, Pause, CheckSquare, Square, Crown, Target, Zap,
  BookOpen, MessageSquare, Clipboard, AlertCircle, Info
} from 'lucide-react';
import { ChecklistItemWithProof } from '../../../components/hive/build/ProofUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status badge component
const StatusBadge = ({ status }) => {
  const config = {
    not_started: { bg: 'bg-zinc-700', text: 'text-zinc-300', label: 'Not Started' },
    in_progress: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'In Progress' },
    submitted: { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'Submitted' },
    under_review: { bg: 'bg-purple-500/20', text: 'text-purple-400', label: 'Under Review' },
    approved: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Approved' },
    needs_revision: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Needs Revision' },
  };
  const c = config[status] || config.not_started;
  return (
    <span className={`px-3 py-1 text-sm font-medium ${c.bg} ${c.text}`}>
      {c.label}
    </span>
  );
};

// Collapsible section component
const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = false, accentColor = '#D4AF37' }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border border-white/10 bg-[#0D0D0D]">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5" style={{ color: accentColor }} />
          <span className="text-white font-bold uppercase tracking-wider">{title}</span>
        </div>
        {isOpen ? (
          <ChevronDown className="w-5 h-5 text-white/50" />
        ) : (
          <ChevronRight className="w-5 h-5 text-white/50" />
        )}
      </button>
      {isOpen && (
        <div className="p-4 pt-0 border-t border-white/10">
          {children}
        </div>
      )}
    </div>
  );
};

// Checklist item component
const ChecklistItemRow = ({ item, checked, onToggle, onProofUpload }) => (
  <div className={`flex items-start gap-3 p-3 border-l-4 ${checked ? 'border-green-500 bg-green-500/5' : 'border-white/20 bg-white/5'}`}>
    <button 
      onClick={onToggle} 
      className="mt-0.5"
      data-testid={`checklist-${item.id}`}
    >
      {checked ? (
        <CheckSquare className="w-5 h-5 text-green-400" />
      ) : (
        <Square className="w-5 h-5 text-white/40 hover:text-white/60" />
      )}
    </button>
    <div className="flex-1">
      <p className={`${checked ? 'text-white/60 line-through' : 'text-white'}`}>{item.text}</p>
      {item.proof_required && !checked && (
        <div className="mt-2 flex items-center gap-2">
          <button
            onClick={onProofUpload}
            className="flex items-center gap-1 px-3 py-1 bg-[#D4AF37]/20 text-[#D4AF37] text-xs hover:bg-[#D4AF37]/30"
            data-testid={`proof-upload-${item.id}`}
          >
            <Upload className="w-3 h-3" />
            Upload {item.proof_type || 'proof'}
          </button>
        </div>
      )}
    </div>
    {item.required && (
      <span className="text-xs text-red-400 uppercase">Required</span>
    )}
  </div>
);

// Script card component
const ScriptCard = ({ script }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = () => {
    navigator.clipboard.writeText(script.script_text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  const typeConfig = {
    phone: { icon: Phone, color: '#10B981', label: 'Phone Script' },
    email: { icon: Mail, color: '#3B82F6', label: 'Email Template' },
    follow_up: { icon: MessageSquare, color: '#F59E0B', label: 'Follow-Up Script' },
  };
  
  const config = typeConfig[script.script_type] || typeConfig.phone;
  const Icon = config.icon;
  
  return (
    <div className="bg-[#121212] border border-white/10 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4" style={{ color: config.color }} />
          <span className="text-xs uppercase" style={{ color: config.color }}>{config.label}</span>
        </div>
        <button
          onClick={handleCopy}
          className="text-xs text-white/50 hover:text-white"
        >
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      <h4 className="text-white font-bold mb-2">{script.title}</h4>
      <p className="text-white/50 text-sm mb-3">{script.scenario}</p>
      <div className="bg-[#0A0A0A] p-3 font-mono text-sm text-white/80 whitespace-pre-wrap">
        {script.script_text}
      </div>
      {script.variables && script.variables.length > 0 && (
        <p className="mt-2 text-xs text-white/40">
          Replace: {script.variables.map(v => `[${v}]`).join(', ')}
        </p>
      )}
    </div>
  );
};

// Cost breakdown table
const CostBreakdown = ({ costs, totalCost, totalTime }) => (
  <div className="bg-[#121212] border border-white/10">
    <div className="p-4 border-b border-white/10">
      <h4 className="text-white font-bold uppercase tracking-wider flex items-center gap-2">
        <DollarSign className="w-4 h-4 text-green-400" />
        Cost & Time Breakdown
      </h4>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-[#0A0A0A]">
          <tr className="text-left text-white/50 text-xs uppercase">
            <th className="p-3">Item</th>
            <th className="p-3">Gov Fee</th>
            <th className="p-3">Service Fee</th>
            <th className="p-3">Total</th>
            <th className="p-3">Processing</th>
            <th className="p-3">Expedited</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {costs.map((cost, idx) => (
            <tr key={idx} className="hover:bg-white/5">
              <td className="p-3 text-white">{cost.item}</td>
              <td className="p-3 text-white/70">${cost.government_fee || 0}</td>
              <td className="p-3 text-white/70">${cost.service_fee || 0}</td>
              <td className="p-3 text-green-400 font-bold">${cost.total_cost}</td>
              <td className="p-3 text-white/70">{cost.processing_time}</td>
              <td className="p-3 text-amber-400 text-sm">
                {cost.expedited_time ? `${cost.expedited_time} (+$${cost.expedited_cost})` : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <div className="p-4 border-t border-white/10 bg-[#0A0A0A] flex items-center justify-between">
      <div>
        <span className="text-white/50 text-sm">Total Estimated Cost:</span>
        <span className="text-2xl font-bold text-green-400 ml-3">${totalCost}</span>
      </div>
      <div>
        <span className="text-white/50 text-sm">Total Time:</span>
        <span className="text-white font-bold ml-3">{totalTime}</span>
      </div>
    </div>
  </div>
);

// Execution step component
const ExecutionStep = ({ step, isComplete, onComplete }) => (
  <div className={`border-l-4 ${isComplete ? 'border-green-500' : 'border-[#D4AF37]'} bg-[#121212] p-6`}>
    <div className="flex items-start justify-between mb-4">
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${isComplete ? 'bg-green-500 text-white' : 'bg-[#D4AF37] text-black'}`}>
          {step.step_number}
        </div>
        <h4 className="text-lg font-bold text-white">{step.title}</h4>
      </div>
      <button
        onClick={onComplete}
        className={`px-3 py-1 text-sm font-medium ${isComplete ? 'bg-green-500/20 text-green-400' : 'bg-white/10 text-white/60 hover:bg-[#D4AF37]/20 hover:text-[#D4AF37]'}`}
      >
        {isComplete ? 'Completed' : 'Mark Complete'}
      </button>
    </div>
    
    <p className="text-white/70 mb-4">{step.description}</p>
    
    {step.url && (
      <a
        href={step.url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black font-medium hover:bg-[#F5D47B] mb-4"
      >
        <ExternalLink className="w-4 h-4" />
        Open Official Website
      </a>
    )}
    
    {step.field_guidance && step.field_guidance.length > 0 && (
      <div className="mt-4 space-y-3">
        <h5 className="text-white/50 text-sm uppercase font-bold">Field-by-Field Guide:</h5>
        {step.field_guidance.map((field, idx) => (
          <div key={idx} className="bg-[#0A0A0A] p-3 border-l-2 border-[#D4AF37]">
            <p className="text-[#D4AF37] font-medium text-sm">{field.field}</p>
            <p className="text-white/80 text-sm mt-1">{field.guidance}</p>
          </div>
        ))}
      </div>
    )}
    
    {step.warnings && step.warnings.length > 0 && (
      <div className="mt-4 bg-red-500/10 border border-red-500/30 p-4">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="w-4 h-4 text-red-400" />
          <span className="text-red-400 font-bold text-sm uppercase">Do NOT:</span>
        </div>
        <ul className="space-y-1">
          {step.warnings.map((warning, idx) => (
            <li key={idx} className="text-red-300/80 text-sm flex items-start gap-2">
              <span className="text-red-400">•</span>
              {warning}
            </li>
          ))}
        </ul>
      </div>
    )}
    
    {step.tips && step.tips.length > 0 && (
      <div className="mt-4 bg-green-500/10 border border-green-500/30 p-4">
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle className="w-4 h-4 text-green-400" />
          <span className="text-green-400 font-bold text-sm uppercase">Pro Tips:</span>
        </div>
        <ul className="space-y-1">
          {step.tips.map((tip, idx) => (
            <li key={idx} className="text-green-300/80 text-sm flex items-start gap-2">
              <span className="text-green-400">•</span>
              {tip}
            </li>
          ))}
        </ul>
      </div>
    )}
  </div>
);

// Main Task Execution Page
const TaskExecutionPage = () => {
  const navigate = useNavigate();
  const { taskId } = useParams();
  const [task, setTask] = useState(null);
  const [progress, setProgress] = useState(null);
  const [valueJustification, setValueJustification] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [completedSteps, setCompletedSteps] = useState({});
  const [checklistProgress, setChecklistProgress] = useState({});
  const [proofs, setProofs] = useState({});  // Store proofs by item key
  
  // Fetch proofs for current task
  const fetchProofs = useCallback(async () => {
    try {
      const token = localStorage.getItem('hive_token');
      if (!token) return;
      
      const res = await axios.get(`${API}/build-system/proof/me/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Organize proofs by checklist:item key
      const proofsByItem = {};
      for (const proof of res.data.proofs || []) {
        const key = `${proof.checklist_id}:${proof.checklist_item_id}`;
        // Keep only the most recent proof per item
        if (!proofsByItem[key] || new Date(proof.submitted_at) > new Date(proofsByItem[key].submitted_at)) {
          proofsByItem[key] = proof;
        }
      }
      setProofs(proofsByItem);
    } catch (err) {
      console.error('Failed to fetch proofs:', err);
    }
  }, [taskId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem('hive_token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        // Fetch task content
        const taskRes = await axios.get(`${API}/build-system/task/${taskId}`, { headers });
        setTask(taskRes.data);
        
        // Fetch user's tier from lifecycle status to get correct value justification
        let userTier = 'foundation';
        try {
          const lifecycleRes = await axios.get(`${API}/hive/me/lifecycle-status`, { headers });
          userTier = lifecycleRes.data?.build?.tier || 'foundation';
        } catch (e) {
          // Default to foundation if can't get tier
        }
        
        // Fetch value justification for user's tier
        try {
          const valueRes = await axios.get(`${API}/build-system/value-justification/${userTier}`, { headers });
          setValueJustification(valueRes.data);
        } catch (e) {
          // Value justification is optional
        }
        
        // Fetch user progress from API
        try {
          const progressRes = await axios.get(`${API}/build-system/progress/me/${taskId}`, { headers });
          const progress = progressRes.data;
          
          // Set checklist progress
          if (progress.checklist_progress) {
            setChecklistProgress(progress.checklist_progress);
          }
          
          // Set completed steps
          if (progress.completed_steps) {
            const stepsMap = {};
            progress.completed_steps.forEach(step => {
              stepsMap[step] = true;
            });
            setCompletedSteps(stepsMap);
          }
        } catch (e) {
          // Progress not found - user hasn't started this task yet
          setChecklistProgress({});
          setCompletedSteps({});
        }
        
        // Fetch proofs
        await fetchProofs();
        
      } catch (err) {
        console.error('Error fetching task:', err);
        setError(err.response?.data?.detail || 'Failed to load task');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [taskId, fetchProofs]);

  const handleStepComplete = async (stepNumber) => {
    const newCompletedSteps = {
      ...completedSteps,
      [stepNumber]: !completedSteps[stepNumber]
    };
    setCompletedSteps(newCompletedSteps);
    
    // Persist to backend
    try {
      const token = localStorage.getItem('hive_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Convert to array of completed step numbers
      const completedStepsArray = Object.entries(newCompletedSteps)
        .filter(([_, isComplete]) => isComplete)
        .map(([stepNum, _]) => parseInt(stepNum));
      
      await axios.post(`${API}/build-system/progress/me/${taskId}/steps`, {
        completed_steps: completedStepsArray
      }, { headers });
    } catch (err) {
      console.error('Error saving step progress:', err);
      // Revert on error
      setCompletedSteps(completedSteps);
    }
  };

  const handleChecklistToggle = async (checklistType, itemId) => {
    const newProgress = {
      ...checklistProgress,
      [checklistType]: {
        ...(checklistProgress[checklistType] || {}),
        [itemId]: !checklistProgress[checklistType]?.[itemId]
      }
    };
    setChecklistProgress(newProgress);
    
    // Persist to backend
    try {
      const token = localStorage.getItem('hive_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.post(`${API}/build-system/progress/me/${taskId}/checklist`, {
        checklist_progress: newProgress
      }, { headers });
    } catch (err) {
      console.error('Error saving checklist progress:', err);
      // Revert on error
      setChecklistProgress(checklistProgress);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <Crown className="w-12 h-12 text-[#D4AF37] animate-pulse mx-auto mb-4" />
          <p className="text-white/50">Loading execution guide...</p>
        </div>
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-white mb-4">{error || 'Task not found'}</p>
          <button
            onClick={() => navigate('/hive/dashboard')}
            className="px-6 py-3 bg-[#D4AF37] text-black font-bold"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const totalSteps = task.execution_steps?.length || 0;
  const completedCount = Object.values(completedSteps).filter(Boolean).length;
  const progressPercent = totalSteps > 0 ? Math.round((completedCount / totalSteps) * 100) : 0;

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="task-execution-page">
      {/* Sticky Header */}
      <header className="bg-[#0D0D0D] border-b border-white/10 px-6 py-4 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigate('/hive/dashboard')}
              className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Build</span>
            </button>
            <StatusBadge status={progress?.status || 'not_started'} />
          </div>
          
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#D4AF37]/20 flex items-center justify-center">
              <Target className="w-6 h-6 text-[#D4AF37]" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                {task.task_name}
              </h1>
              <div className="flex items-center gap-4 mt-1">
                <span className="text-white/50 text-sm">Phase {task.phase}</span>
                {task.day_in_roadmap && (
                  <>
                    <span className="text-white/30">•</span>
                    <span className="text-[#D4AF37] text-sm">Day {task.day_in_roadmap} of 7-Day Launch</span>
                  </>
                )}
              </div>
            </div>
            <div className="text-right">
              <p className="text-white/50 text-sm">Progress</p>
              <p className="text-2xl font-bold text-[#D4AF37]">{progressPercent}%</p>
            </div>
          </div>
          
          {/* Progress bar */}
          <div className="mt-4 h-2 bg-[#1a1a1a] rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-[#D4AF37] to-[#F5D47B] transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-8 space-y-8">
        {/* Strategic Overview */}
        {task.strategic_overview && (
          <CollapsibleSection title="Strategic Overview" icon={BookOpen} defaultOpen={true}>
            <div className="space-y-6 pt-4">
              {/* Why It Matters */}
              <div>
                <h4 className="text-[#D4AF37] font-bold uppercase text-sm mb-2">Why This Step Matters</h4>
                <p className="text-white/80">{task.strategic_overview.why_it_matters}</p>
              </div>
              
              {/* Long Term Impact */}
              {task.strategic_overview.long_term_impact && (
                <div>
                  <h4 className="text-green-400 font-bold uppercase text-sm mb-2">Long-Term Impact</h4>
                  <p className="text-white/80">{task.strategic_overview.long_term_impact}</p>
                </div>
              )}
              
              {/* Compliance Connection */}
              {task.strategic_overview.compliance_connection && (
                <div>
                  <h4 className="text-blue-400 font-bold uppercase text-sm mb-2">Compliance Connection</h4>
                  <p className="text-white/80">{task.strategic_overview.compliance_connection}</p>
                </div>
              )}
              
              {/* Common Mistakes */}
              {task.strategic_overview.common_mistakes && task.strategic_overview.common_mistakes.length > 0 && (
                <div className="bg-red-500/10 border border-red-500/30 p-4">
                  <h4 className="text-red-400 font-bold uppercase text-sm mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Common Rookie Mistakes (That Cost Thousands)
                  </h4>
                  <ul className="space-y-2">
                    {task.strategic_overview.common_mistakes.map((mistake, idx) => (
                      <li key={idx} className="text-red-300/80 flex items-start gap-2">
                        <span className="text-red-400 font-bold">×</span>
                        {mistake}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Cost of Failure */}
              {task.strategic_overview.cost_of_failure && (
                <div className="bg-amber-500/10 border border-amber-500/30 p-4">
                  <h4 className="text-amber-400 font-bold uppercase text-sm mb-2 flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Cost of Getting This Wrong
                  </h4>
                  <p className="text-amber-300/80">{task.strategic_overview.cost_of_failure}</p>
                </div>
              )}
            </div>
          </CollapsibleSection>
        )}

        {/* Pre-Filing Checklist */}
        {task.checklists?.pre_filing && task.checklists.pre_filing.length > 0 && (
          <CollapsibleSection title="Pre-Filing Checklist" icon={Clipboard} defaultOpen={true} accentColor="#F59E0B">
            <div className="space-y-2 pt-4">
              {task.checklists.pre_filing.map((item) => (
                <ChecklistItemWithProof
                  key={item.id}
                  item={item}
                  taskId={taskId}
                  checklistId="pre_filing"
                  checked={checklistProgress.pre_filing?.[item.id] || false}
                  onToggle={() => handleChecklistToggle('pre_filing', item.id)}
                  proof={proofs[`pre_filing:${item.id}`]}
                  onProofChange={fetchProofs}
                />
              ))}
            </div>
          </CollapsibleSection>
        )}

        {/* Step-by-Step Execution */}
        {task.execution_steps && task.execution_steps.length > 0 && (
          <CollapsibleSection title="Step-by-Step Execution Guide" icon={Target} defaultOpen={true} accentColor="#10B981">
            <div className="space-y-4 pt-4">
              {task.execution_steps.map((step) => (
                <ExecutionStep
                  key={step.step_number}
                  step={step}
                  isComplete={completedSteps[step.step_number] || false}
                  onComplete={() => handleStepComplete(step.step_number)}
                />
              ))}
            </div>
          </CollapsibleSection>
        )}

        {/* Post-Filing Checklist */}
        {task.checklists?.post_filing && task.checklists.post_filing.length > 0 && (
          <CollapsibleSection title="Post-Filing Verification" icon={CheckCircle} accentColor="#10B981">
            <div className="space-y-2 pt-4">
              {task.checklists.post_filing.map((item) => (
                <ChecklistItemWithProof
                  key={item.id}
                  item={item}
                  taskId={taskId}
                  checklistId="post_filing"
                  checked={checklistProgress.post_filing?.[item.id] || false}
                  onToggle={() => handleChecklistToggle('post_filing', item.id)}
                  proof={proofs[`post_filing:${item.id}`]}
                  onProofChange={fetchProofs}
                />
              ))}
            </div>
          </CollapsibleSection>
        )}

        {/* Script Vault */}
        {task.scripts && task.scripts.length > 0 && (
          <CollapsibleSection title="Script Vault" icon={MessageSquare} accentColor="#8B5CF6">
            <div className="grid gap-4 pt-4">
              {task.scripts.map((script, idx) => (
                <ScriptCard key={idx} script={script} />
              ))}
            </div>
          </CollapsibleSection>
        )}

        {/* Cost Breakdown */}
        {task.cost_breakdown && task.cost_breakdown.length > 0 && (
          <CostBreakdown 
            costs={task.cost_breakdown}
            totalCost={task.total_estimated_cost}
            totalTime={task.total_estimated_time}
          />
        )}

        {/* Required Documents */}
        {task.required_documents && task.required_documents.length > 0 && (
          <CollapsibleSection title="Required Documents" icon={FileText} accentColor="#3B82F6">
            <div className="pt-4">
              <ul className="space-y-2">
                {task.required_documents.map((doc, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-white/80">
                    <FileText className="w-4 h-4 text-blue-400" />
                    {doc}
                  </li>
                ))}
              </ul>
            </div>
          </CollapsibleSection>
        )}

        {/* Proof Requirements */}
        {task.proof_requirements && task.proof_requirements.length > 0 && (
          <CollapsibleSection title="Proof Upload Requirements" icon={Upload} accentColor="#D4AF37">
            <div className="space-y-4 pt-4">
              {task.proof_requirements.map((proof, idx) => (
                <div key={idx} className="bg-[#121212] border border-white/10 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-[#D4AF37] text-sm uppercase font-bold">{proof.type}</span>
                      <p className="text-white mt-1">{proof.description}</p>
                    </div>
                    <button className="px-4 py-2 bg-[#D4AF37] text-black font-medium hover:bg-[#F5D47B]">
                      <Upload className="w-4 h-4 inline mr-2" />
                      Upload
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </CollapsibleSection>
        )}

        {/* Why This Is Worth $997 */}
        {valueJustification && (
          <div className="bg-gradient-to-br from-[#D4AF37]/20 to-[#0D0D0D] border-2 border-[#D4AF37]/50 p-6">
            <div className="flex items-center gap-3 mb-4">
              <Crown className="w-8 h-8 text-[#D4AF37]" />
              <div>
                <h3 className="text-xl font-bold text-white">Why This Is Worth ${valueJustification.price}</h3>
                <p className="text-[#D4AF37]">{valueJustification.subheadline}</p>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              {valueJustification.value_points?.map((point, idx) => (
                <div key={idx} className="bg-[#0A0A0A]/50 p-4">
                  <h4 className="text-white font-bold mb-1">{point.title}</h4>
                  <p className="text-white/60 text-sm mb-2">{point.description}</p>
                  <p className="text-green-400 font-bold">Savings: {point.savings}</p>
                </div>
              ))}
            </div>
            
            <div className="bg-[#0A0A0A] p-4 text-center">
              <p className="text-white/50 text-sm">Total Potential Savings</p>
              <p className="text-3xl font-bold text-green-400">{valueJustification.total_potential_savings}</p>
              <p className="text-white/70 mt-2">{valueJustification.roi_statement}</p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-8 border-t border-white/10">
          <button
            onClick={() => navigate('/hive/dashboard')}
            className="px-6 py-3 bg-white/10 text-white hover:bg-white/20"
          >
            Save & Exit
          </button>
          <button
            onClick={() => console.log('Mark task complete')}
            className="px-8 py-3 bg-[#D4AF37] text-black font-bold hover:bg-[#F5D47B]"
          >
            <CheckCircle className="w-5 h-5 inline mr-2" />
            Mark Task Complete
          </button>
        </div>
      </main>
    </div>
  );
};

export default TaskExecutionPage;
